// StepLocation - Address, City, Postal Code with Map Integration

import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, Navigation, Crosshair, ChevronDown, CheckCircle2, AlertCircle } from 'lucide-react';
import { googleMapsService, type GeocodedLocation } from '../../../services/googleMapsService';
import { LocationAutocomplete } from '../../location/LocationAutocomplete';

interface FormData {
  address: string;
  postalCode: string;
  city: string;
  latitude: number | null;
  longitude: number | null;
}

interface StepLocationProps {
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
}

export function StepLocation({ formData, updateFormData }: StepLocationProps) {
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(
    formData.latitude && formData.longitude
      ? { lat: formData.latitude, lng: formData.longitude }
      : null
  );
  const [isGeocodingAddress, setIsGeocodingAddress] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [nearbyAddress, setNearbyAddress] = useState<string | undefined>();
  const [showManualCoords, setShowManualCoords] = useState(false);
  const [manualLat, setManualLat] = useState<string>(formData.latitude?.toString() || '');
  const [manualLng, setManualLng] = useState<string>(formData.longitude?.toString() || '');
  const [coordError, setCoordError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  // Validate coordinates for Germany region
  const validateCoordinates = (lat: string, lng: string): boolean => {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);

    if (isNaN(latNum) || isNaN(lngNum)) {
      setCoordError('Bitte gültige Zahlen eingeben');
      return false;
    }

    // Germany/DACH region: ca. 45-56 Lat, 5-17 Lng
    if (latNum < 45 || latNum > 56) {
      setCoordError('Breitengrad muss zwischen 45 und 56 liegen (DACH-Region)');
      return false;
    }

    if (lngNum < 5 || lngNum > 17) {
      setCoordError('Längengrad muss zwischen 5 und 17 liegen (DACH-Region)');
      return false;
    }

    setCoordError(null);
    return true;
  };

  // Handle manual coordinates application
  const handleApplyManualCoords = async () => {
    if (validateCoordinates(manualLat, manualLng)) {
      const lat = parseFloat(manualLat);
      const lng = parseFloat(manualLng);

      setCoordinates({ lat, lng });
      updateFormData({
        latitude: lat,
        longitude: lng,
      });

      // Optional: Reverse-Geocoding um Adresse zu ermitteln
      try {
        const result = await googleMapsService.reverseGeocode(lat, lng);
        if (result) {
          const addressParts = result.formattedAddress.split(',');
          updateFormData({
            address: addressParts[0]?.trim() || formData.address,
            postalCode: result.postalCode || formData.postalCode,
            city: result.city || formData.city,
            latitude: lat,
            longitude: lng,
          });
          setNearbyAddress(result.formattedAddress);
        }
      } catch (error) {
        console.error('Reverse geocoding error:', error);
      }
    }
  };

  // Geocode address when it changes
  useEffect(() => {
    const geocodeAddress = async () => {
      if (!formData.address || !formData.city) {
        setCoordinates(null);
        return;
      }

      setIsGeocodingAddress(true);
      try {
        const fullAddress = `${formData.address}, ${formData.postalCode || ''} ${formData.city}`;
        const result = await googleMapsService.geocodeAddress(fullAddress);
        if (result) {
          setCoordinates({ lat: result.latitude, lng: result.longitude });
          // Update formData with coordinates
          updateFormData({
            latitude: result.latitude,
            longitude: result.longitude,
          });
          // Update manual input fields
          setManualLat(result.latitude.toString());
          setManualLng(result.longitude.toString());
        }
      } catch (error) {
        console.error('Geocoding error:', error);
      } finally {
        setIsGeocodingAddress(false);
      }
    };

    // Debounce geocoding
    const timeoutId = setTimeout(geocodeAddress, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.address, formData.city, formData.postalCode, updateFormData]);

  // Initialize map when coordinates change
  useEffect(() => {
    if (!coordinates || !mapContainerRef.current) return;

    const initMap = async () => {
      try {
        const { setOptions, importLibrary } = await import('@googlemaps/js-api-loader');
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        if (!apiKey || apiKey === 'YOUR_API_KEY_HERE') return;

        setOptions({
          key: apiKey,
          v: 'weekly',
          libraries: ['maps', 'marker'],
          language: 'de',
          region: 'DE',
        });

        await importLibrary('maps');
        await importLibrary('marker');

        // Create map if not exists
        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new google.maps.Map(mapContainerRef.current!, {
            center: { lat: coordinates.lat, lng: coordinates.lng },
            zoom: 16,
            disableDefaultUI: true,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            mapId: 'goodplace-wizard-map',
          });
        } else {
          mapInstanceRef.current.panTo({ lat: coordinates.lat, lng: coordinates.lng });
        }

        // Update or create marker
        if (markerRef.current) {
          markerRef.current.position = { lat: coordinates.lat, lng: coordinates.lng };
        } else {
          const markerElement = document.createElement('div');
          markerElement.innerHTML = `
            <div class="w-10 h-10 bg-indigo-500 rounded-full shadow-lg flex items-center justify-center border-2 border-white cursor-grab active:cursor-grabbing">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
            </div>
          `;

          markerRef.current = new google.maps.marker.AdvancedMarkerElement({
            map: mapInstanceRef.current,
            position: { lat: coordinates.lat, lng: coordinates.lng },
            content: markerElement,
            gmpDraggable: true, // Draggable marker
          });

          // Add drag event listener
          markerRef.current.addListener('dragend', async () => {
            const newPosition = markerRef.current?.position;
            if (newPosition && typeof newPosition === 'object' && 'lat' in newPosition) {
              const newLat = typeof newPosition.lat === 'function' ? newPosition.lat() : newPosition.lat;
              const newLng = typeof newPosition.lng === 'function' ? newPosition.lng() : newPosition.lng;

              // Update coordinates state
              setCoordinates({ lat: newLat, lng: newLng });
              setManualLat(newLat.toString());
              setManualLng(newLng.toString());

              // Update form data
              updateFormData({
                latitude: newLat,
                longitude: newLng,
              });

              // Reverse geocode for new address
              try {
                const result = await googleMapsService.reverseGeocode(newLat, newLng);
                if (result) {
                  const addressParts = result.formattedAddress.split(',');
                  updateFormData({
                    address: addressParts[0]?.trim() || formData.address,
                    postalCode: result.postalCode || formData.postalCode,
                    city: result.city || formData.city,
                  });
                  setNearbyAddress(result.formattedAddress);
                }
              } catch (error) {
                console.error('Reverse geocoding error after drag:', error);
              }
            }
          });
        }
      } catch (error) {
        console.error('Map initialization error:', error);
      }
    };

    initMap();
  }, [coordinates]);

  // Handle location selection from autocomplete
  const handleLocationSelect = (location: GeocodedLocation) => {
    // Parse address into components
    const addressParts = location.formattedAddress.split(',');
    const streetAddress = addressParts[0]?.trim() || '';

    updateFormData({
      address: streetAddress,
      postalCode: location.postalCode || formData.postalCode,
      city: location.city || formData.city,
      latitude: location.latitude,
      longitude: location.longitude,
    });

    setCoordinates({ lat: location.latitude, lng: location.longitude });
    setManualLat(location.latitude.toString());
    setManualLng(location.longitude.toString());
  };

  // Handle nearby location click (GPS)
  const handleNearbyClick = () => {
    if (!navigator.geolocation) {
      setLocationError('Standortermittlung wird von diesem Browser nicht unterstützt');
      return;
    }

    setIsLocating(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setCoordinates({ lat: latitude, lng: longitude });
        setManualLat(latitude.toString());
        setManualLng(longitude.toString());

        // Reverse geocode to get address
        try {
          const result = await googleMapsService.reverseGeocode(latitude, longitude);
          if (result) {
            const addressParts = result.formattedAddress.split(',');
            updateFormData({
              address: addressParts[0]?.trim() || '',
              postalCode: result.postalCode || '',
              city: result.city || '',
              latitude: latitude,
              longitude: longitude,
            });
            setNearbyAddress(result.formattedAddress);
            setLocationQuery(addressParts[0]?.trim() || '');
          } else {
            // No reverse geocode result, but still save coordinates
            updateFormData({
              latitude: latitude,
              longitude: longitude,
            });
          }
        } catch (error) {
          console.error('Reverse geocoding error:', error);
          // Still save coordinates even if reverse geocoding fails
          updateFormData({
            latitude: latitude,
            longitude: longitude,
          });
        }
        setIsLocating(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsLocating(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Standortzugriff wurde verweigert. Bitte erlaube den Zugriff in deinen Browser-Einstellungen.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Standort konnte nicht ermittelt werden.');
            break;
          case error.TIMEOUT:
            setLocationError('Standortermittlung hat zu lange gedauert.');
            break;
          default:
            setLocationError('Ein unbekannter Fehler ist aufgetreten.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const hasValidAddress = formData.address && formData.city;
  const showMap = googleMapsService.isAvailable() && (coordinates || hasValidAddress);
  const hasCoordinates = formData.latitude !== null && formData.longitude !== null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Wo befindet sich der Ort?
        </h2>
        <p className="text-gray-600">
          Erfasse die Koordinaten des GoodPlace. Die Adresse wird automatisch ermittelt und kann angepasst werden.
        </p>
      </div>

      {/* Coordinate Status Banner */}
      <div className={`flex items-center gap-3 p-4 rounded-xl ${
        hasCoordinates
          ? 'bg-green-50 border border-green-200'
          : 'bg-amber-50 border border-amber-200'
      }`}>
        {hasCoordinates ? (
          <>
            <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-green-800">Standort erfasst</p>
              <p className="text-sm text-green-700">
                {formData.latitude?.toFixed(4)}, {formData.longitude?.toFixed(4)}
                {formData.city && ` (${formData.city})`}
              </p>
            </div>
          </>
        ) : (
          <>
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-800">Standort noch nicht erfasst</p>
              <p className="text-sm text-amber-700">
                Nutze GPS, Adresssuche oder gib Koordinaten manuell ein.
              </p>
            </div>
          </>
        )}
      </div>

      {/* GPS Location Section - Prominent */}
      <div className="bg-indigo-50 border-2 border-indigo-200 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
            <Navigation className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900">GPS-Standort nutzen</h4>
            <p className="text-sm text-gray-600">
              Schnellste Methode: Nutze deinen aktuellen Standort
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleNearbyClick}
          disabled={isLocating}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-500 text-white rounded-lg font-medium hover:bg-indigo-600 transition disabled:opacity-50"
        >
          {isLocating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Standort wird ermittelt...
            </>
          ) : (
            <>
              <Crosshair className="w-5 h-5" />
              Meinen Standort verwenden
            </>
          )}
        </button>

        {nearbyAddress && (
          <p className="text-sm text-indigo-700 mt-2 text-center">
            Gefunden: {nearbyAddress}
          </p>
        )}

        {locationError && (
          <p className="text-sm text-red-600 mt-2 text-center">
            {locationError}
          </p>
        )}
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-3 bg-white text-gray-500">oder Adresse eingeben</span>
        </div>
      </div>

      {/* Address - Optional */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Straße und Hausnummer
          <span className="text-gray-400 font-normal text-xs ml-2">(optional, wird automatisch ermittelt)</span>
        </label>
        <div className="relative">
          <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={formData.address}
            onChange={(e) => updateFormData({ address: e.target.value })}
            placeholder="z.B. Musterstraße 123"
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* City and Postal Code - Optional */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            PLZ
            <span className="text-gray-400 font-normal text-xs ml-1">(optional)</span>
          </label>
          <input
            type="text"
            value={formData.postalCode}
            onChange={(e) => {
              // Only allow digits
              const value = e.target.value.replace(/\D/g, '');
              updateFormData({ postalCode: value });
            }}
            placeholder="12345"
            maxLength={5}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none ${
              formData.postalCode && formData.postalCode.length > 0 && formData.postalCode.length !== 5
                ? 'border-amber-300 bg-amber-50'
                : formData.postalCode && formData.postalCode.length === 5
                ? 'border-green-300 bg-green-50'
                : 'border-gray-300'
            }`}
          />
          {formData.postalCode && formData.postalCode.length > 0 && formData.postalCode.length !== 5 && (
            <p className="text-xs text-amber-600 mt-1">PLZ sollte 5 Ziffern haben</p>
          )}
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Stadt
            <span className="text-gray-400 font-normal text-xs ml-1">(optional)</span>
          </label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => updateFormData({ city: e.target.value })}
            placeholder="z.B. Berlin"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* Location Search with Autocomplete */}
      {googleMapsService.isAvailable() && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ort suchen
          </label>
          <LocationAutocomplete
            value={locationQuery}
            onChange={setLocationQuery}
            onSelect={handleLocationSelect}
            placeholder="Suche nach Adresse oder Ort..."
            showNearbyButton={true}
            onNearbyClick={handleNearbyClick}
            isLocating={isLocating}
            nearbyAddress={nearbyAddress}
          />
          <p className="text-xs text-gray-500 mt-1">
            Wähle einen Vorschlag aus um die Adresse automatisch auszufüllen
          </p>
        </div>
      )}

      {/* Map Preview */}
      {showMap ? (
        <div>
          <div className="rounded-xl overflow-hidden border border-gray-200 relative">
            {/* Map Container */}
            <div
              ref={mapContainerRef}
              className="w-full h-48 sm:h-56 bg-gray-100"
            />

            {/* Loading Overlay */}
            {isGeocodingAddress && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Standort wird gesucht...</span>
                </div>
              </div>
            )}

            {/* Coordinates Badge */}
            {coordinates && !isGeocodingAddress && (
              <div className="absolute bottom-3 left-3 px-2 py-1 bg-white rounded-lg shadow-md text-xs text-gray-600">
                <Navigation className="w-3 h-3 inline mr-1" />
                {coordinates.lat.toFixed(4)}, {coordinates.lng.toFixed(4)}
              </div>
            )}
          </div>
          {/* Drag Hint */}
          {coordinates && (
            <p className="text-sm text-gray-500 mt-2 text-center flex items-center justify-center gap-1">
              <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full"></span>
              Position nicht exakt? Ziehe den Marker auf der Karte zur richtigen Stelle.
            </p>
          )}
        </div>
      ) : (
        <div className="bg-gray-100 rounded-xl p-8 text-center">
          <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            {googleMapsService.isAvailable()
              ? 'Gib eine Adresse ein um die Karte anzuzeigen'
              : 'Kartenvorschau nicht verfügbar (API-Key fehlt)'
            }
          </p>
        </div>
      )}

      {/* Manual Coordinates Section (Collapsible) */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setShowManualCoords(!showManualCoords)}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition"
        >
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">
              Koordinaten manuell eingeben
            </span>
            <span className="text-xs text-gray-500">(Power-User)</span>
          </div>
          <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${showManualCoords ? 'rotate-180' : ''}`} />
        </button>

        {showManualCoords && (
          <div className="p-4 border-t border-gray-100 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Breitengrad (Latitude)
                </label>
                <input
                  type="text"
                  value={manualLat}
                  onChange={(e) => setManualLat(e.target.value)}
                  placeholder="z.B. 48.1351"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Längengrad (Longitude)
                </label>
                <input
                  type="text"
                  value={manualLng}
                  onChange={(e) => setManualLng(e.target.value)}
                  placeholder="z.B. 11.5820"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            {coordError && (
              <p className="text-sm text-red-600">{coordError}</p>
            )}

            <button
              type="button"
              onClick={handleApplyManualCoords}
              className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-900 transition"
            >
              Koordinaten übernehmen
            </button>

            <p className="text-xs text-gray-500">
              Tipp: Koordinaten findest du z.B. auf Google Maps (Rechtsklick auf Ort)
            </p>
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Tipps für die Adresse</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>Gib die vollständige Straße und Hausnummer an</li>
          <li>Bei Hinterhöfen oder schwer zu findenden Orten: Erwähne Details in der Beschreibung</li>
          <li>Die Adresse hilft anderen, den Ort auf der Karte zu finden</li>
        </ul>
      </div>
    </div>
  );
}

export default StepLocation;
